import { useState, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { MessageSquare, Search, ChevronLeft, ChevronRight, Eye, Lightbulb, AlertCircle, HelpCircle, Send } from 'lucide-react'
import { format } from 'date-fns'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Input } from '@/components/ui/input'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'

import { supportTicketsApi } from '@/lib/api'
import type { SupportTicketWithUser, TicketType, TicketStatus, TicketPriority, TicketComment } from '@/types'

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100]
const ALL_STATUSES = 'ALL_STATUSES'
const ALL_PRIORITIES = 'ALL_PRIORITIES'

const ticketStatusOptions: TicketStatus[] = ['OPEN', 'IN_PROGRESS', 'RESOLVED', 'CLOSED']
const ticketPriorityOptions: TicketPriority[] = ['LOW', 'MEDIUM', 'HIGH', 'URGENT']

const getStatusBadgeVariant = (status: TicketStatus) => {
  switch (status) {
    case 'OPEN': return 'default'
    case 'IN_PROGRESS': return 'secondary'
    case 'RESOLVED': return 'outline'
    case 'CLOSED': return 'outline'
    default: return 'default'
  }
}

const getPriorityBadgeVariant = (priority: TicketPriority) => {
  switch (priority) {
    case 'URGENT': return 'destructive'
    case 'HIGH': return 'destructive'
    case 'MEDIUM': return 'secondary'
    case 'LOW': return 'outline'
    default: return 'default'
  }
}

const getTypeBadgeVariant = (type: TicketType) => {
  switch (type) {
    case 'QUERY': return 'default'
    case 'ISSUE': return 'destructive'
    case 'SUGGESTION': return 'secondary'
    default: return 'default'
  }
}

export default function AdminSupportTicketsPage() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [activeTab, setActiveTab] = useState<'tickets' | 'suggestions'>('tickets')
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(25)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedStatus, setSelectedStatus] = useState<string>(ALL_STATUSES)
  const [selectedPriority, setSelectedPriority] = useState<string>(ALL_PRIORITIES)
  const [viewTicket, setViewTicket] = useState<SupportTicketWithUser | null>(null)
  const [updateStatus, setUpdateStatus] = useState<TicketStatus>('OPEN')
  const [newComment, setNewComment] = useState('')

  // Fetch all tickets
  const { data: allTickets = [], isLoading } = useQuery<SupportTicketWithUser[]>({
    queryKey: ['admin-support-tickets'],
    queryFn: () => supportTicketsApi.getAllTickets(),
  })

  // Update status mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: TicketStatus }) =>
      supportTicketsApi.updateStatus(id, { status }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-support-tickets'] })
      toast.success(t('support.statusUpdated'))
      setViewTicket(null)
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.message || error.message || t('support.updateError')
      toast.error(errorMessage)
    },
  })

  // Fetch comments for the selected ticket (admin endpoint)
  const { data: comments = [], isLoading: isLoadingComments, refetch: refetchComments } = useQuery<TicketComment[]>({
    queryKey: ['admin-ticket-comments', viewTicket?.ticket_id],
    queryFn: () => supportTicketsApi.listCommentsAdmin(viewTicket!.ticket_id),
    enabled: !!viewTicket,
  })

  // Create comment mutation (admin endpoint)
  const createCommentMutation = useMutation({
    mutationFn: ({ ticketId, commentText }: { ticketId: string; commentText: string }) =>
      supportTicketsApi.createCommentAdmin(ticketId, { comment_text: commentText }),
    onSuccess: () => {
      refetchComments()
      setNewComment('')
      toast.success(t('support.commentAdded'))
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.message || error.message || t('support.commentError')
      toast.error(errorMessage)
    },
  })

  // Separate tickets and suggestions
  const tickets = useMemo(() => allTickets.filter(t => t.ticket_type !== 'SUGGESTION'), [allTickets])
  const suggestions = useMemo(() => allTickets.filter(t => t.ticket_type === 'SUGGESTION'), [allTickets])

  // Filter based on active tab
  const currentData = activeTab === 'tickets' ? tickets : suggestions

  const filteredData = useMemo(() => {
    return currentData.filter(ticket => {
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase()
        if (!ticket.subject.toLowerCase().includes(searchLower) &&
            !ticket.description.toLowerCase().includes(searchLower) &&
            !ticket.user_name.toLowerCase().includes(searchLower) &&
            !ticket.tenant_name.toLowerCase().includes(searchLower)) {
          return false
        }
      }
      if (selectedStatus !== ALL_STATUSES && ticket.status !== selectedStatus) return false
      if (selectedPriority !== ALL_PRIORITIES && ticket.priority !== selectedPriority) return false
      return true
    })
  }, [currentData, searchTerm, selectedStatus, selectedPriority])

  // Pagination
  const totalPages = Math.ceil(filteredData.length / pageSize)
  const paginatedData = filteredData.slice((currentPage - 1) * pageSize, currentPage * pageSize)

  const handleViewTicket = (ticket: SupportTicketWithUser) => {
    setViewTicket(ticket)
    setUpdateStatus(ticket.status)
    setNewComment('')
  }

  const handleUpdateStatus = () => {
    if (!viewTicket) return
    updateMutation.mutate({
      id: viewTicket.ticket_id,
      status: updateStatus,
    })
  }

  const handleAddComment = () => {
    if (!viewTicket || !newComment.trim()) return
    createCommentMutation.mutate({
      ticketId: viewTicket.ticket_id,
      commentText: newComment.trim(),
    })
  }

  const clearFilters = () => {
    setSearchTerm('')
    setSelectedStatus(ALL_STATUSES)
    setSelectedPriority(ALL_PRIORITIES)
    setCurrentPage(1)
  }

  // Stats
  const openTickets = tickets.filter(t => t.status === 'OPEN').length
  const inProgressTickets = tickets.filter(t => t.status === 'IN_PROGRESS').length
  const openSuggestions = suggestions.filter(t => t.status === 'OPEN').length

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <MessageSquare className="h-8 w-8" />
        <div>
          <h1 className="text-2xl font-bold">{t('support.adminTitle')}</h1>
          <p className="text-muted-foreground">{t('support.adminDescription')}</p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">{t('support.openTickets')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-orange-500" />
              <span className="text-2xl font-bold">{openTickets}</span>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">{t('support.inProgress')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <HelpCircle className="h-5 w-5 text-blue-500" />
              <span className="text-2xl font-bold">{inProgressTickets}</span>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">{t('support.totalTickets')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5 text-gray-500" />
              <span className="text-2xl font-bold">{tickets.length}</span>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">{t('support.newSuggestions')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <Lightbulb className="h-5 w-5 text-yellow-500" />
              <span className="text-2xl font-bold">{openSuggestions}</span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs for Tickets vs Suggestions */}
      <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v as 'tickets' | 'suggestions'); setCurrentPage(1) }}>
        <TabsList>
          <TabsTrigger value="tickets" className="flex items-center gap-2">
            <AlertCircle className="h-4 w-4" />
            {t('support.queriesAndIssues')} ({tickets.length})
          </TabsTrigger>
          <TabsTrigger value="suggestions" className="flex items-center gap-2">
            <Lightbulb className="h-4 w-4" />
            {t('support.suggestions')} ({suggestions.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab} className="space-y-4">
          {/* Filters */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex flex-wrap gap-4 items-end">
                <div className="flex-1 min-w-[200px]">
                  <div className="relative">
                    <div className="absolute left-3 top-1/2 -translate-y-1/2 z-10 pointer-events-none bg-white dark:bg-neutral-800 rounded-sm">
                      <Search className="h-4 w-4 text-muted-foreground" />
                    </div>
                    <Input
                      placeholder={t('support.adminSearchPlaceholder')}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">{t('support.status')}</Label>
                  <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                    <SelectTrigger className="w-[150px]">
                      <SelectValue placeholder={t('support.filterStatus')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={ALL_STATUSES}>{t('common.all')}</SelectItem>
                      {ticketStatusOptions.map(status => (
                        <SelectItem key={status} value={status}>{t(`support.statuses.${status}`)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">{t('support.priority')}</Label>
                  <Select value={selectedPriority} onValueChange={setSelectedPriority}>
                    <SelectTrigger className="w-[150px]">
                      <SelectValue placeholder={t('support.filterPriority')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={ALL_PRIORITIES}>{t('common.all')}</SelectItem>
                      {ticketPriorityOptions.map(priority => (
                        <SelectItem key={priority} value={priority}>{t(`support.priorities.${priority}`)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button variant="outline" onClick={clearFilters}>{t('common.clearFilters')}</Button>
              </div>
            </CardContent>
          </Card>

          {/* Table */}
          <Card>
            <CardContent className="p-0">
              <div className="h-[calc(100vh-550px)] overflow-auto">
                <Table>
                  <TableHeader className="sticky top-0 bg-background z-10">
                    <TableRow>
                      <TableHead>{t('support.subject')}</TableHead>
                      {activeTab === 'tickets' && <TableHead>{t('support.type')}</TableHead>}
                      <TableHead>{t('support.user')}</TableHead>
                      <TableHead>{t('support.tenant')}</TableHead>
                      <TableHead>{t('support.status')}</TableHead>
                      <TableHead>{t('support.priority')}</TableHead>
                      <TableHead>{t('support.createdAt')}</TableHead>
                      <TableHead className="text-right">{t('common.actions')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {isLoading ? (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center py-8">{t('common.loading')}</TableCell>
                      </TableRow>
                    ) : paginatedData.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center py-8">{t('support.noTickets')}</TableCell>
                      </TableRow>
                    ) : (
                      paginatedData.map(ticket => (
                        <TableRow key={ticket.ticket_id}>
                          <TableCell className="font-medium max-w-[200px] truncate">{ticket.subject}</TableCell>
                          {activeTab === 'tickets' && (
                            <TableCell>
                              <Badge variant={getTypeBadgeVariant(ticket.ticket_type)}>
                                {t(`support.types.${ticket.ticket_type}`)}
                              </Badge>
                            </TableCell>
                          )}
                          <TableCell>{ticket.user_name}</TableCell>
                          <TableCell>{ticket.tenant_name}</TableCell>
                          <TableCell>
                            <Badge variant={getStatusBadgeVariant(ticket.status)}>
                              {t(`support.statuses.${ticket.status}`)}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge variant={getPriorityBadgeVariant(ticket.priority)}>
                              {t(`support.priorities.${ticket.priority}`)}
                            </Badge>
                          </TableCell>
                          <TableCell>{format(new Date(ticket.created_at), 'MMM dd, yyyy')}</TableCell>
                          <TableCell className="text-right">
                            <Button variant="ghost" size="icon" onClick={() => handleViewTicket(ticket)}>
                              <Eye className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>

          {/* Pagination */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">{t('common.rowsPerPage')}</span>
              <Select value={pageSize.toString()} onValueChange={(v) => { setPageSize(Number(v)); setCurrentPage(1) }}>
                <SelectTrigger className="w-[70px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PAGE_SIZE_OPTIONS.map(size => (
                    <SelectItem key={size} value={size.toString()}>{size}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">
                {t('common.pageOf', { current: currentPage, total: totalPages || 1 })}
              </span>
              <Button variant="outline" size="icon" disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button variant="outline" size="icon" disabled={currentPage >= totalPages} onClick={() => setCurrentPage(p => p + 1)}>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* View/Update Ticket Dialog */}
      <Dialog open={!!viewTicket} onOpenChange={() => setViewTicket(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{viewTicket?.subject}</DialogTitle>
          </DialogHeader>
          {viewTicket && (
            <div className="space-y-4">
              <div className="flex gap-2 flex-wrap">
                <Badge variant={getTypeBadgeVariant(viewTicket.ticket_type)}>{t(`support.types.${viewTicket.ticket_type}`)}</Badge>
                <Badge variant={getStatusBadgeVariant(viewTicket.status)}>{t(`support.statuses.${viewTicket.status}`)}</Badge>
                <Badge variant={getPriorityBadgeVariant(viewTicket.priority)}>{t(`support.priorities.${viewTicket.priority}`)}</Badge>
              </div>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div><span className="text-muted-foreground">{t('support.user')}:</span> {viewTicket.user_name}</div>
                <div><span className="text-muted-foreground">{t('support.tenant')}:</span> {viewTicket.tenant_name}</div>
              </div>
              <div>
                <Label className="text-muted-foreground">{t('support.descriptionLabel')}</Label>
                <p className="mt-1 whitespace-pre-wrap bg-muted p-3 rounded-md text-sm">{viewTicket.description}</p>
              </div>
              <div className="border-t pt-4 space-y-4">
                <div className="flex items-center gap-4">
                  <div className="flex-1 space-y-2">
                    <Label>{t('support.updateStatus')}</Label>
                    <Select value={updateStatus} onValueChange={(v) => setUpdateStatus(v as TicketStatus)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {ticketStatusOptions.map(status => (
                          <SelectItem key={status} value={status}>{t(`support.statuses.${status}`)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="pt-6">
                    <Button onClick={handleUpdateStatus} disabled={updateMutation.isPending || updateStatus === viewTicket.status}>
                      {updateMutation.isPending ? t('common.updating') : t('support.updateStatusBtn')}
                    </Button>
                  </div>
                </div>
              </div>

              {/* Comments Section */}
              <div className="border-t pt-4 space-y-3">
                <Label>{t('support.comments')}</Label>
                <div className="h-[200px] overflow-y-auto border rounded-md p-3">
                  {isLoadingComments ? (
                    <div className="text-center text-muted-foreground py-4">{t('common.loading')}</div>
                  ) : comments.length === 0 ? (
                    <div className="text-center text-muted-foreground py-4">{t('support.noComments')}</div>
                  ) : (
                    <div className="space-y-3">
                      {comments.map((comment) => (
                        <div key={comment.comment_id} className="bg-muted p-3 rounded-md">
                          <div className="flex items-center justify-between mb-1">
                            <span className="font-medium text-sm">{comment.user_name}</span>
                            <span className="text-xs text-muted-foreground">
                              {format(new Date(comment.created_at), 'MMM dd, yyyy HH:mm')}
                            </span>
                          </div>
                          <p className="text-sm whitespace-pre-wrap">{comment.comment_text}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex gap-2">
                  <Textarea
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    placeholder={t('support.addCommentPlaceholder')}
                    rows={2}
                    className="flex-1"
                  />
                  <Button
                    onClick={handleAddComment}
                    disabled={createCommentMutation.isPending || !newComment.trim()}
                    size="icon"
                    className="h-auto"
                  >
                    <Send className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              <div className="text-sm text-muted-foreground">
                {t('support.createdAt')}: {format(new Date(viewTicket.created_at), 'MMM dd, yyyy HH:mm')}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setViewTicket(null)}>{t('common.close')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

